<script setup lang="ts">
import UserBadge from '@/components/Profile/UserBadge.vue';
import { useDayOff } from '@/composables/useDayOff';
import type { ApprovedDayOffGroup, ApprovedDaysOffByUser, DayOffApproval } from '@/types/api';
import { formatDateRange } from '@/utils/time';
import Button from 'primevue/button';
import Card from 'primevue/card';
import Dialog from 'primevue/dialog';
// import Dropdown from 'primevue/dropdown';
// import IconField from 'primevue/iconfield';
// import InputIcon from 'primevue/inputicon';
// import InputText from 'primevue/inputtext';
import Skeleton from 'primevue/skeleton';
import Textarea from 'primevue/textarea';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

// Use the day off composable
const {
  pendingApprovals,
  approvedDaysOff,
  loadingPending,
  loadingApproved,
  fetchPendingApprovals,
  fetchApprovedDaysOff,
  approveOrDeclineDaysOff,
  confirmApprovalAction,
  confirmDeleteApproved,
} = useDayOff();

const showCommentDialog = ref(false);
const selectedGroup = ref<DayOffApproval | null>(null);
const comment = ref('');
const actionType = ref<'approve' | 'decline'>('approve');
const submitting = ref(false);

// Filter states
const searchQuery = ref('');
const selectedDateFilter = ref<string>('all');
const selectedSortOption = ref<string>('date-asc');

// Filter options
// const dateFilterOptions = computed(() => [
//   { label: t('dayOff.filters.allDates'), value: 'all' },
//   { label: t('dayOff.filters.thisWeek'), value: 'thisWeek' },
//   { label: t('dayOff.filters.thisMonth'), value: 'thisMonth' },
//   { label: t('dayOff.filters.nextMonth'), value: 'nextMonth' },
// ]);

// const sortOptions = computed(() => [
//   { label: t('dayOff.filters.dateAscending'), value: 'date-asc' },
//   { label: t('dayOff.filters.dateDescending'), value: 'date-desc' },
//   { label: t('dayOff.filters.employeeName'), value: 'name' },
//   { label: t('dayOff.filters.daysCount'), value: 'days' },
// ]);

// Filtered and sorted approved days off
const filteredApprovedDaysOff = computed(() => {
  let filtered = [...approvedDaysOff.value];

  // Search filter
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.toLowerCase();
    filtered = filtered.filter((userGroup) => `${userGroup.user.first_name} ${userGroup.user.last_name}`.toLowerCase().includes(query));
  }

  // Date range filter
  if (selectedDateFilter.value !== 'all') {
    const now = new Date();
    let startDate: Date;
    let endDate: Date;

    if (selectedDateFilter.value === 'thisWeek') {
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
      startDate = new Date(now.setDate(diff));
      endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 6);
    } else if (selectedDateFilter.value === 'thisMonth') {
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else if (selectedDateFilter.value === 'nextMonth') {
      startDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      endDate = new Date(now.getFullYear(), now.getMonth() + 2, 0);
    } else {
      startDate = now;
      endDate = new Date(now.getFullYear() + 1, now.getMonth(), now.getDate());
    }

    filtered = filtered
      .map((userGroup) => ({
        ...userGroup,
        approved_days_off_groups: userGroup.approved_days_off_groups.filter((group) => {
          const groupDates = group.dates.map((d) => new Date(d));
          return groupDates.some((date) => date >= startDate && date <= endDate);
        }),
        total_days: userGroup.approved_days_off_groups
          .filter((group) => {
            const groupDates = group.dates.map((d) => new Date(d));
            return groupDates.some((date) => date >= startDate && date <= endDate);
          })
          .reduce((sum, group) => sum + group.dates.length, 0),
      }))
      .filter((userGroup) => userGroup.approved_days_off_groups.length > 0);
  }

  // Sort
  if (selectedSortOption.value === 'date-asc') {
    filtered.sort((a, b) => {
      const aDate = new Date(a.approved_days_off_groups[0]?.dates[0] || 0);
      const bDate = new Date(b.approved_days_off_groups[0]?.dates[0] || 0);
      return aDate.getTime() - bDate.getTime();
    });
  } else if (selectedSortOption.value === 'date-desc') {
    filtered.sort((a, b) => {
      const aDate = new Date(a.approved_days_off_groups[0]?.dates[0] || 0);
      const bDate = new Date(b.approved_days_off_groups[0]?.dates[0] || 0);
      return bDate.getTime() - aDate.getTime();
    });
  } else if (selectedSortOption.value === 'name') {
    filtered.sort((a, b) => {
      const aName = `${a.user.first_name} ${a.user.last_name}`;
      const bName = `${b.user.first_name} ${b.user.last_name}`;
      return aName.localeCompare(bName);
    });
  } else if (selectedSortOption.value === 'days') {
    filtered.sort((a, b) => b.total_days - a.total_days);
  }

  return filtered;
});

function openCommentDialog(group: DayOffApproval, action: 'approve' | 'decline') {
  selectedGroup.value = group;
  actionType.value = action;
  comment.value = '';
  // For now, submit directly without comment dialog
  // If you want to enable comment dialog, uncomment the next line
  // showCommentDialog.value = true;
  submitAction();
}

function handleConfirmAction(group: DayOffApproval, action: 'approve' | 'decline') {
  confirmApprovalAction(group, action, () => {
    openCommentDialog(group, action);
  });
}

async function submitAction() {
  if (!selectedGroup.value) return;

  submitting.value = true;
  try {
    await approveOrDeclineDaysOff(selectedGroup.value, actionType.value, comment.value || undefined);

    showCommentDialog.value = false;
    selectedGroup.value = null;
    comment.value = '';
  } finally {
    submitting.value = false;
  }
}

function handleDeleteApproved(userGroup: ApprovedDaysOffByUser, approvedGroup: ApprovedDayOffGroup) {
  confirmDeleteApproved(userGroup, approvedGroup);
}

onMounted(() => {
  fetchPendingApprovals();
  fetchApprovedDaysOff();
});
</script>

<template>
  <div class="container mx-auto px-4 py-6">
    <!-- <div class="mb-6">
      <h1 class="mb-2 font-bold text-2xl text-gray-900">{{ t('dayOff.dayOffManagement') }}</h1>
      <p class="text-gray-600">{{ t('dayOff.dayOffManagementDescription') }}</p>
    </div> -->

    <!-- Pending Approvals Section -->
    <div class="mb-8">
      <h2 class="mb-4 font-semibold text-xl text-gray-900">{{ t('dayOff.pendingApprovals') }}</h2>

      <!-- Loading State for Pending -->
      <div v-if="loadingPending" class="space-y-4">
        <Skeleton v-for="i in 3" :key="i" height="150px" />
      </div>

      <!-- Empty State for Pending -->
      <div v-else-if="pendingApprovals.length === 0" class="py-4">
        <!-- <div class="mb-4 text-gray-400">
          <i class="pi pi-calendar-times text-3xl"></i>
        </div> -->
        <!-- <h3 class="mb-2 text-lg font-medium text-gray-900">{{ t('dayOff.noPendingApprovals') }}</h3> -->
        <p class="italic text-gray-500">{{ t('dayOff.noPendingApprovalsDescription') }}</p>
      </div>

      <!-- Pending Approvals List -->
      <div v-else class="space-y-3">
        <Card v-for="group in pendingApprovals" :key="group.group_id" class="w-full rounded-lg border border-orange-200 p-3">
          <template #content>
            <div class="flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
              <div class="flex flex-1 flex-col gap-1.5">
                <!-- User Info -->
                <UserBadge :user="group.user" :show-name="true" :color="group.user.kitchen_position?.color" />

                <!-- Request Details -->
                <div class="flex flex-col gap-1.5">
                  <div class="flex flex-row flex-wrap items-center justify-between py-1 text-sm">
                    <div>
                      <i class="pi pi-calendar mr-1.5 text-xs text-gray-400"></i>
                      <span class="font-medium">{{ formatDateRange(group.dates) }}</span>
                    </div>
                    <span class="ml-1.5 text-gray-500"
                      >({{ group.dates.length }} {{ group.dates.length === 1 ? t('dayOff.day') : t('dayOff.days') }})</span
                    >
                  </div>

                  <div v-if="group.reason" class="flex items-start text-sm">
                    <i class="pi pi-tag mr-1.5 mt-0.5 text-xs text-gray-400"></i>
                    <span class="font-medium text-gray-600">{{ t(`dayOff.reasons.${group.reason.name}`) }}</span>
                  </div>

                  <div v-if="group.info" class="flex items-start text-sm">
                    <i class="pi pi-info-circle mr-1.5 mt-0.5 text-xs text-gray-400"></i>
                    <span class="text-gray-600">{{ group.info }}</span>
                  </div>
                </div>
              </div>

              <!-- Action Buttons -->
              <div class="flex w-full flex-row items-center justify-end gap-1.5 md:w-auto">
                <Button
                  icon="pi pi-check"
                  severity="success"
                  :label="t('dayOff.approve')"
                  size="small"
                  @click="handleConfirmAction(group, 'approve')"
                />
                <Button
                  icon="pi pi-times"
                  severity="danger"
                  :label="t('dayOff.decline')"
                  size="small"
                  outlined
                  @click="handleConfirmAction(group, 'decline')"
                />
              </div>
            </div>
          </template>
        </Card>
      </div>
    </div>

    <!-- Approved Days Off Section -->
    <div class="mb-8">
      <div class="mb-4 flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
        <h2 class="font-semibold text-xl text-gray-900">{{ t('dayOff.approvedDaysOff') }}</h2>

        <!-- Filters -->
        <!-- <div class="flex w-full flex-col gap-2 md:w-auto md:flex-row"> -->
        <!-- Search -->
        <!-- <InputText icon="pi pi-search" v-model="searchQuery" :placeholder="t('dayOff.filters.searchEmployee')"
            class="w-full" /> -->

        <!-- Date Filter -->
        <!-- <Dropdown v-model="selectedDateFilter" :options="dateFilterOptions" option-label="label" option-value="value"
            :placeholder="t('dayOff.filters.dateRange')" class="w-full md:w-48" /> -->

        <!-- Sort -->
        <!-- <Dropdown v-model="selectedSortOption" :options="sortOptions" option-label="label" option-value="value"
            :placeholder="t('dayOff.filters.sortBy')" class="w-full md:w-48" /> -->
        <!-- </div> -->
      </div>

      <!-- Loading State for Approved -->
      <div v-if="loadingApproved" class="space-y-4">
        <Skeleton v-for="i in 3" :key="i" height="120px" />
      </div>

      <!-- Empty State for Approved -->
      <div v-else-if="filteredApprovedDaysOff.length === 0 && approvedDaysOff.length === 0" class="py-4">
        <!-- <div class="mb-4 text-gray-400">
          <i class="pi pi-calendar-check text-5xl"></i>
        </div>
        <h3 class="mb-2 text-lg font-medium text-gray-900">{{ t('dayOff.noApprovedDaysOff') }}</h3> -->
        <p class="italic text-gray-500">{{ t('dayOff.noApprovedDaysOffDescription') }}</p>
      </div>

      <!-- No Results State -->
      <div v-else-if="filteredApprovedDaysOff.length === 0" class="py-8 text-center">
        <div class="mb-4 text-gray-400">
          <i class="pi pi-filter-slash text-5xl"></i>
        </div>
        <h3 class="mb-2 text-lg font-medium text-gray-900">{{ t('dayOff.filters.noResults') }}</h3>
        <p class="text-gray-500">{{ t('dayOff.filters.noResultsDescription') }}</p>
        <Button
          :label="t('dayOff.filters.clearFilters')"
          text
          size="small"
          class="mt-4"
          @click="
            searchQuery = '';
            selectedDateFilter = 'all';
          "
        />
      </div>

      <!-- Approved Days Off List -->
      <div v-else class="space-y-4">
        <Card v-for="userGroup in filteredApprovedDaysOff" :key="userGroup.user.id" class="w-full rounded-xl border border-green-200 bg-green-50 p-4">
          <template #content>
            <div class="flex flex-col gap-4">
              <!-- User Info -->
              <div class="flex items-center justify-between">
                <UserBadge :user="userGroup.user" :show-name="true" :color="userGroup.user.kitchen_position?.color" />
                <span class="rounded bg-white px-2 py-1 text-sm text-gray-500">
                  {{ userGroup.total_days }} {{ userGroup.total_days === 1 ? t('dayOff.day') : t('dayOff.days') }}
                </span>
              </div>

              <!-- Approved Groups -->
              <div class="space-y-3">
                <div
                  v-for="approvedGroup in userGroup.approved_days_off_groups"
                  :key="approvedGroup.group_id"
                  class="rounded-lg border border-gray-200 bg-white p-3"
                >
                  <div class="flex flex-col gap-2">
                    <div class="flex items-center justify-between">
                      <div class="flex items-center text-sm">
                        <i class="pi pi-calendar mr-2 text-gray-400"></i>
                        <span class="font-medium">{{ formatDateRange(approvedGroup.dates) }}</span>
                        <span class="ml-2 text-gray-500"
                          >({{ approvedGroup.dates.length }} {{ approvedGroup.dates.length === 1 ? t('dayOff.day') : t('dayOff.days') }})</span
                        >
                      </div>
                      <div class="flex items-center gap-2">
                        <!-- <span class="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                          {{ t('dayOff.approved') }}
                        </span> -->
                        <Button
                          icon="pi pi-trash"
                          severity="danger"
                          text
                          size="small"
                          :aria-label="t('common.delete')"
                          @click="handleDeleteApproved(userGroup, approvedGroup)"
                        />
                      </div>
                    </div>

                    <div v-if="approvedGroup.info" class="flex items-start text-sm">
                      <i class="pi pi-info-circle mr-2 mt-0.5 text-gray-400"></i>
                      <span class="text-gray-600">{{ approvedGroup.info }}</span>
                    </div>

                    <!-- <div v-if="approvedGroup.approved_by" class="flex items-center text-xs text-gray-500">
                      <i class="pi pi-user text-gray-400 mr-2"></i>
                      <span>{{ t('dayOff.approvedBy') }} {{ approvedGroup.approved_by.first_name }} {{
                        approvedGroup.approved_by.last_name }}</span>
                      <span class="ml-2">{{ dayjs(approvedGroup.approved_at).format('DD/MM/YYYY HH:mm') }}</span>
                    </div> -->
                  </div>
                </div>
              </div>
            </div>
          </template>
        </Card>
      </div>
    </div>

    <!-- Comment Dialog -->
    <Dialog
      v-model:visible="showCommentDialog"
      :header="actionType === 'approve' ? t('dayOff.approveRequest') : t('dayOff.declineRequest')"
      modal
      class="w-96"
      :style="{ maxWidth: '90vw' }"
    >
      <div class="space-y-4">
        <p class="text-sm text-gray-600">
          {{ actionType === 'approve' ? t('dayOff.approveCommentDescription') : t('dayOff.declineCommentDescription') }}
        </p>

        <div>
          <label for="comment" class="mb-1 block text-sm font-medium text-gray-700">
            {{ t('dayOff.comment') }}
          </label>
          <Textarea id="comment" v-model="comment" :placeholder="t('dayOff.commentPlaceholder')" rows="3" class="w-full" />
        </div>
      </div>

      <template #footer>
        <div class="flex justify-end space-x-2">
          <Button :label="t('common.cancel')" severity="secondary" outlined :disabled="submitting" @click="showCommentDialog = false" />
          <Button
            :label="actionType === 'approve' ? t('dayOff.approve') : t('dayOff.decline')"
            :severity="actionType === 'approve' ? 'success' : 'danger'"
            :loading="submitting"
            @click="submitAction"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>
